import { useId, type HTMLAttributes } from 'react';
import { cn } from '@/utils/cn';
import { Icon } from '../Icon';
import { FormField } from '../FormField/FormField';
import { Typography } from '../Typography';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RadioSelectOption {
  /** The value that identifies this option. */
  value: string;
  /** Human-readable label rendered inside the option row. */
  label: string;
  /** When true the option is non-interactive and rendered in a disabled style. */
  disabled?: boolean;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface RadioSelectProps extends Omit<HTMLAttributes<HTMLUListElement>, 'onChange'> {
  /**
   * Array of options to render.
   * Each option must have a unique `value` string.
   */
  options: RadioSelectOption[];
  /**
   * The currently selected value, or `undefined` when nothing is selected.
   * The component is fully controlled — the consumer owns selection state.
   */
  value: string | undefined;
  /**
   * Called when the user selects an option. Receives the newly selected value.
   * Selecting the already-selected option is a no-op (radio semantics).
   * Disabled options never fire `onChange`.
   */
  onChange: (value: string) => void;
  /** Field label — rendered as a `<label>` above the list via FormField. */
  label?: string;
  /**
   * Error message. When provided:
   * - Replaces `helperText` in the text slot below the list
   * - Text is rendered in `text-error` colour
   */
  error?: string;
  /**
   * Helper text shown below the list when there is no `error`.
   * Rendered in `text-bodylight` colour.
   */
  helperText?: string;
}

// ─── Option row styles ────────────────────────────────────────────────────────

const optionBase =
  'flex items-center gap-4 px-4 py-4 rounded-small border cursor-pointer transition-colors duration-150 select-none';

const optionDefault = 'border-default bg-surface';

const optionSelected = 'border-interactive-moderate bg-interactive-quiet';

const optionDisabled = 'border-transparent bg-disabled cursor-not-allowed';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer RadioSelect component.
 *
 * A fully controlled list of single-select option rows. Each row shows a radio
 * icon on the left and a label on the right. Clicking a row selects it and
 * deselects any previously selected row (radio semantics — only one option can
 * be active at a time).
 *
 * ### States per option row
 * - **Default** — `border-default`, `bg-surface`, `text-body`, `radio` icon
 * - **Selected** — `border-interactive-moderate`, `bg-interactive-quiet`,
 *   `text-interactive-hover-text`, `radio-selected` icon
 * - **Disabled** — `bg-disabled`, no border, `text-disabled`, `radio` icon
 *   in `disabled` colour; not interactive
 *
 * ### Accessibility
 * The list is rendered as a `<ul role="radiogroup">` with each option as a
 * `<li role="radio">`. The `aria-checked` and `aria-disabled` attributes are
 * set on each option. The group is labelled via `aria-labelledby` when a
 * `label` prop is provided.
 *
 * @example
 * // Basic controlled usage
 * const [selected, setSelected] = useState<string | undefined>(undefined);
 * <RadioSelect
 *   label="Retailer"
 *   options={[
 *     { value: 'bj', label: "BJ's Wholesale Club" },
 *     { value: 'costco', label: 'Costco' },
 *   ]}
 *   value={selected}
 *   onChange={setSelected}
 * />
 *
 * @example
 * // With a disabled option
 * <RadioSelect
 *   options={[
 *     { value: 'a', label: 'Option A' },
 *     { value: 'b', label: 'Option B', disabled: true },
 *   ]}
 *   value={selected}
 *   onChange={setSelected}
 * />
 *
 * @see src/primer/README.md
 */
export function RadioSelect({
  options,
  value,
  onChange,
  label,
  error,
  helperText,
  ...props
}: RadioSelectProps) {
  const labelId = useId();

  function handleSelect(optionValue: string) {
    if (optionValue !== value) {
      onChange(optionValue);
    }
  }

  return (
    <FormField label={label} error={error} helperText={helperText} inputId={labelId}>
      <ul
        role="radiogroup"
        aria-labelledby={label ? labelId : undefined}
        className="flex flex-col gap-3 list-none p-0 m-0"
        {...props}
      >
        {options.map((option) => {
          const isSelected = option.value === value;
          const isDisabled = Boolean(option.disabled);

          return (
            <li
              key={option.value}
              role="radio"
              aria-checked={isSelected}
              aria-disabled={isDisabled}
              tabIndex={isDisabled ? -1 : 0}
              onClick={() => !isDisabled && handleSelect(option.value)}
              onKeyDown={(e) => {
                if (!isDisabled && (e.key === ' ' || e.key === 'Enter')) {
                  e.preventDefault();
                  handleSelect(option.value);
                }
              }}
              className={cn(
                optionBase,
                isDisabled ? optionDisabled : isSelected ? optionSelected : optionDefault
              )}
              data-radioselect-option=""
              data-selected={isSelected || undefined}
              data-disabled={isDisabled || undefined}
            >
              {/* Radio icon */}
              <Icon
                name={isSelected ? 'radio-selected' : 'radio'}
                size="large"
                color={isDisabled ? 'disabled' : 'interactive'}
                aria-hidden
              />

              {/* Label */}
              <Typography
                variant="body"
                size="medium"
                color={isDisabled ? 'disabled' : isSelected ? 'interactivehover' : 'body'}
              >
                {option.label}
              </Typography>
            </li>
          );
        })}
      </ul>
    </FormField>
  );
}

export default RadioSelect;
